/**
 * DropZonePlugin - Drag/Drop support for TinyMCE
 *
 * Handles:
 * - Converting <!-- drop --> comments to interactive drop zones
 * - Visual drop indicators during drag operations
 * - Template drops into editor and nested zones
 * - Cleanup of empty zones back to comments on save
 *
 * Ported from: _archived_custom_editor/RichTextLoader.js
 */

import { resolveTemplate } from '../utils/TemplateSchemaResolver.js';

export class DropZonePlugin {
    constructor(editor) {
        this.editor = editor;
        this.dropIndicator = null;
        this.currentDropTarget = null;
        this.lastKnownY = null;
    }

    /**
     * Initialize the plugin
     */
    init() {
        console.log('🎯 DropZonePlugin initializing for editor:', this.editor.id);
        console.log('🎯 Editor body:', this.editor.getBody());

        this.injectStyles();
        this.setupContentProcessing();
        this.setupDragHandlers();
        this.setupFormCleanup();

        console.log('✅ DropZonePlugin initialized successfully');
        console.log('✅ Drag handlers attached to:', this.editor.getBody());
    }

    /**
     * Inject CSS styles for drop zones and indicators
     */
    injectStyles() {
        const styleId = `dropzone-styles-${this.editor.id}`;

        // CRITICAL: Inject into TinyMCE iframe's document, not main document
        // TinyMCE editor is in an iframe with its own document
        const editorDoc = this.editor.getDoc();
        const targetHead = editorDoc.head || editorDoc.getElementsByTagName('head')[0];

        // Check if styles already injected in iframe
        if (editorDoc.getElementById(styleId)) {
            console.log('⚠️  Drop zone styles already injected in iframe, skipping');
            return;
        }

        const styleEl = editorDoc.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            /* Drop zone animations */
            @keyframes dropPulse {
                0%, 100% {
                    opacity: 0.9;
                    transform: scaleY(1);
                    box-shadow: 0 0 12px rgba(59, 130, 246, 0.8);
                }
                50% {
                    opacity: 1;
                    transform: scaleY(1.3);
                    box-shadow: 0 0 20px rgba(59, 130, 246, 1);
                }
            }

            @keyframes gradientShift {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
            }

            /* Drop indicator */
            .richtext-drop-indicator {
                position: absolute !important;
                left: 0 !important;
                right: 0 !important;
                height: 6px !important;
                background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6) !important;
                background-size: 200% 100% !important;
                border-radius: 3px !important;
                animation: dropPulse 0.8s ease-in-out infinite, gradientShift 2s linear infinite !important;
                box-shadow: 0 0 12px rgba(59, 130, 246, 0.8) !important;
                z-index: 1000 !important;
                pointer-events: none !important;
            }

            .richtext-drop-indicator::before {
                content: "DROP HERE" !important;
                position: absolute !important;
                top: -24px !important;
                left: 50% !important;
                transform: translateX(-50%) !important;
                background: #3b82f6 !important;
                color: white !important;
                padding: 4px 12px !important;
                border-radius: 4px !important;
                font-size: 11px !important;
                font-weight: 600 !important;
                white-space: nowrap !important;
            }

            /* Nested drop zones */
            .richtext-nested-dropzone {
                min-height: 80px;
                border: 2px dashed #d1d5db;
                border-radius: 8px;
                margin: 12px 0;
                padding: 20px;
                text-align: center;
                color: #9ca3af;
                background: #f9fafb;
                transition: all 0.2s ease;
                cursor: pointer;
                position: relative;
            }

            .richtext-nested-dropzone:hover {
                border-color: #3b82f6;
                background-color: #eff6ff;
            }

            .richtext-nested-dropzone span {
                font-size: 14px;
                font-weight: 500;
                pointer-events: none;
            }
        `;

        targetHead.appendChild(styleEl);
        console.log('💅 Drop zone styles injected into TinyMCE iframe:', styleId);
    }

    /**
     * Setup content processing - convert <!-- drop --> to zones on SetContent
     */
    setupContentProcessing() {
        this.editor.on('SetContent', (e) => {
            if (e.content && e.content.includes('<!-- drop -->')) {
                const body = this.editor.getBody();
                const processedHTML = this.processDropZones(body.innerHTML, this.editor.id);
                body.innerHTML = processedHTML;
            }
        });

        // Also process initial content
        this.editor.on('init', () => {
            const body = this.editor.getBody();
            if (body.innerHTML.includes('<!-- drop -->')) {
                const processedHTML = this.processDropZones(body.innerHTML, this.editor.id);
                body.innerHTML = processedHTML;
            }
        });

        // Clean up when getting content for save
        this.editor.on('GetContent', (e) => {
            e.content = this.cleanupForSave(e.content);
        });
    }

    /**
     * Process HTML to convert <!-- drop --> comments to interactive drop zones
     * @param {string} html - HTML content
     * @param {string} parentId - Parent element ID for zone IDs
     * @returns {string} Processed HTML
     */
    processDropZones(html, parentId) {
        if (!html || typeof html !== 'string') {
            return html;
        }

        const dropMarkerRegex = /<!--\s*drop\s*-->/gi;
        const matches = [...html.matchAll(dropMarkerRegex)];

        if (matches.length === 0) {
            return html;
        }

        let processedHTML = html;
        let zoneIndex = 0;

        processedHTML = processedHTML.replace(dropMarkerRegex, () => {
            const zoneId = `${parentId}-dropzone-${zoneIndex++}`;
            return `<div class="richtext-nested-dropzone"
                         data-zone-id="${zoneId}"
                         data-parent-id="${parentId}"
                         contenteditable="false"
                         data-mce-contenteditable="false"
                         style="min-height: 80px; border: 2px dashed #d1d5db; border-radius: 8px;
                                margin: 12px 0; padding: 20px; text-align: center;
                                color: #9ca3af; background: #f9fafb;
                                transition: all 0.2s ease; cursor: pointer;
                                position: relative;">
                <span style="font-size: 14px; font-weight: 500; pointer-events: none;">Drop template here</span>
            </div>`;
        });

        console.log(`📍 Processed ${matches.length} drop zones in ${parentId}`);
        return processedHTML;
    }

    /**
     * Setup drag handlers for the editor
     */
    setupDragHandlers() {
        const editorBody = this.editor.getBody();
        console.log('🔧 Setting up drag handlers on editor body:', editorBody);

        // Dragover - show visual indicator and handle nested zones
        editorBody.addEventListener('dragover', (e) => {
            // Check if we're over a nested drop zone first
            const dropZone = e.target.closest('.richtext-nested-dropzone');
            if (dropZone) {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'copy';

                // Highlight the drop zone
                dropZone.style.borderColor = '#3b82f6';
                dropZone.style.backgroundColor = '#eff6ff';

                // Hide main drop indicator when over nested zone
                if (this.dropIndicator) {
                    this.dropIndicator.style.display = 'none';
                }
                return;
            }

            // Main editor dragover - show drop indicator
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';

            this.updateDropIndicator(e);
        }, true); // Use capture phase

        // Dragleave - reset nested zone styling
        editorBody.addEventListener('dragleave', (e) => {
            const dropZone = e.target.closest('.richtext-nested-dropzone');
            if (dropZone) {
                // Only reset if we're actually leaving the zone
                if (!dropZone.contains(e.relatedTarget)) {
                    dropZone.style.borderColor = '#d1d5db';
                    dropZone.style.backgroundColor = '#f9fafb';
                }
            }

            // Remove main drop indicator if leaving editor
            if (!editorBody.contains(e.relatedTarget)) {
                this.removeDropIndicator();
            }
        }, true);

        // Drop - handle template drops
        editorBody.addEventListener('drop', async (e) => {
            // Check if we're dropping into a nested drop zone
            const dropZone = e.target.closest('.richtext-nested-dropzone');
            if (dropZone) {
                await this.handleNestedDrop(e, dropZone);
                return;
            }

            // Main editor drop
            await this.handleMainDrop(e);
        }, true);

        // Dragend - cleanup
        editorBody.addEventListener('dragend', () => {
            this.removeDropIndicator();
        });
    }

    /**
     * Update drop indicator position based on cursor
     * @param {DragEvent} e - Drag event
     */
    updateDropIndicator(e) {
        this.lastKnownY = e.clientY;
        const editorBody = this.editor.getBody();

        // Find closest element to cursor position
        let closestElement = null;
        let closestDistance = Infinity;
        let insertBefore = true;

        const elements = Array.from(editorBody.children).filter(
            el => !el.classList.contains('richtext-drop-indicator')
        );
        elements.forEach(el => {
            const rect = el.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;
            const distance = Math.abs(e.clientY - midpoint);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestElement = el;
                insertBefore = e.clientY < midpoint;
            }
        });

        // Create or show drop indicator
        if (!this.dropIndicator) {
            this.dropIndicator = document.createElement('div');
            this.dropIndicator.className = 'richtext-drop-indicator';
        }

        // Position indicator
        if (closestElement) {
            const rect = closestElement.getBoundingClientRect();
            const editorRect = editorBody.getBoundingClientRect();

            if (insertBefore) {
                this.dropIndicator.style.top = (rect.top - editorRect.top + editorBody.scrollTop) + 'px';
            } else {
                this.dropIndicator.style.top = (rect.bottom - editorRect.top + editorBody.scrollTop) + 'px';
            }

            this.currentDropTarget = { element: closestElement, insertBefore };
        } else {
            // Empty editor or drop at end
            this.dropIndicator.style.top = '0px';
            this.currentDropTarget = { element: null, insertBefore: true };
        }

        // Add to DOM if not already present
        if (!this.dropIndicator.parentNode) {
            editorBody.appendChild(this.dropIndicator);
        }

        this.dropIndicator.style.display = 'block';
    }

    /**
     * Remove drop indicator from DOM
     */
    removeDropIndicator() {
        if (this.dropIndicator && this.dropIndicator.parentNode) {
            this.dropIndicator.remove();
        }
        this.dropIndicator = null;
        this.currentDropTarget = null;
    }

    /**
     * Handle drop into main editor
     * @param {DragEvent} e - Drop event
     */
    async handleMainDrop(e) {
        e.preventDefault();
        e.stopPropagation();

        const { htmlContent, templateId, templateName } = await this.parseDragData(e);

        if (!htmlContent) {
            console.warn('No HTML content in drop data');
            this.removeDropIndicator();
            return;
        }

        // Generate unique ID for this template instance
        const instanceId = `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create wrapper div with tracking attributes
        const templateWrapper = document.createElement('div');
        templateWrapper.className = 'template-wrapper';
        templateWrapper.id = instanceId;
        templateWrapper.setAttribute('data-template-id', templateId || 'unknown');
        templateWrapper.setAttribute('data-template-name', templateName || 'Template');
        templateWrapper.setAttribute('data-template-instance', instanceId);
        templateWrapper.setAttribute('contenteditable', 'true');
        templateWrapper.style.margin = '20px 0';
        templateWrapper.style.cursor = 'pointer';

        // Process template HTML for nested drop zones
        const processedHTML = this.processDropZones(htmlContent, instanceId);
        templateWrapper.innerHTML = processedHTML;

        // Remove properties script from rendered content
        const propsScript = templateWrapper.querySelector('script[data-template-properties]');
        if (propsScript) {
            propsScript.remove();
        }

        // Insert at drop indicator position
        const editorBody = this.editor.getBody();
        if (this.currentDropTarget && this.currentDropTarget.element) {
            if (this.currentDropTarget.insertBefore) {
                editorBody.insertBefore(templateWrapper, this.currentDropTarget.element);
            } else {
                if (this.currentDropTarget.element.nextSibling) {
                    editorBody.insertBefore(templateWrapper, this.currentDropTarget.element.nextSibling);
                } else {
                    editorBody.appendChild(templateWrapper);
                }
            }
        } else {
            // Empty editor or append
            editorBody.appendChild(templateWrapper);
        }

        // Register template element for click handling
        if (this.editor.templateClickPlugin) {
            this.editor.templateClickPlugin.registerTemplateElement(templateWrapper, 'WYSIWYG');
        }

        // Cleanup
        this.removeDropIndicator();

        // Trigger change event
        this.editor.dispatch('change');

        // Clear template data
        if (window.templateDragData) {
            window.templateDragData = null;
        }

        console.log('✅ Template dropped:', instanceId, templateName);
    }

    /**
     * Handle drop into nested drop zone
     * @param {DragEvent} e - Drop event
     * @param {HTMLElement} dropZone - Drop zone element
     */
    async handleNestedDrop(e, dropZone) {
        e.preventDefault();
        e.stopPropagation();

        // Reset drop zone styling
        dropZone.style.borderColor = '#d1d5db';
        dropZone.style.backgroundColor = '#f9fafb';

        const zoneId = dropZone.getAttribute('data-zone-id');
        const parentId = dropZone.getAttribute('data-parent-id');

        const { htmlContent, templateId, templateName } = await this.parseDragData(e);

        if (!htmlContent) {
            console.warn('No HTML content in nested drop');
            return;
        }

        // Generate unique ID for this nested template instance
        const instanceId = `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create wrapper div with tracking attributes
        const templateWrapper = document.createElement('div');
        templateWrapper.className = 'template-wrapper';
        templateWrapper.id = instanceId;
        templateWrapper.setAttribute('data-template-id', templateId || 'unknown');
        templateWrapper.setAttribute('data-template-name', templateName || 'Nested Template');
        templateWrapper.setAttribute('data-template-instance', instanceId);
        templateWrapper.setAttribute('contenteditable', 'true');
        templateWrapper.style.margin = '0 0 8px 0';

        // Process template HTML for nested drop zones
        const processedHTML = this.processDropZones(htmlContent, instanceId);
        templateWrapper.innerHTML = processedHTML;

        // Remove properties script
        const propsScript = templateWrapper.querySelector('script[data-template-properties]');
        if (propsScript) {
            propsScript.remove();
        }

        // Insert the dropped template BEFORE the dropzone so multiple blocks can stack
        // in the same column. The dropzone is kept as a trailing slot for further drops.
        dropZone.parentNode.insertBefore(templateWrapper, dropZone);

        // Register template element for click handling
        if (this.editor.templateClickPlugin) {
            this.editor.templateClickPlugin.registerTemplateElement(templateWrapper, 'WYSIWYG');
        }

        // Trigger change event
        this.editor.dispatch('change');

        // Clear template data
        if (window.templateDragData) {
            window.templateDragData = null;
        }

        console.log('✅ Nested template dropped:', instanceId, 'into zone:', zoneId);
    }

    /**
     * Parse drag data from drop event
     * @param {DragEvent} e - Drop event
     * @returns {Object} { htmlContent, templateId, templateName }
     */
    async parseDragData(e) {
        // Try multiple data transfer keys - different drag sources use different formats
        const dragDataText = e.dataTransfer.getData('text/plain');
        const templateIdFromCustom = e.dataTransfer.getData('template-id');
        const templateNameFromCustom = e.dataTransfer.getData('template-name');

        let htmlContent = null;
        let templateId = templateIdFromCustom || null; // Use custom key first
        let templateName = templateNameFromCustom || null;

        // Try to parse as JSON first (AlpineBlocks template format)
        try {
            const dragData = JSON.parse(dragDataText);
            if (dragData.type === 'template' && dragData.data) {
                // Check if this is a lazy template that needs loading
                if (dragData.data._templateRef) {
                    const template = window._alpineTemplates?.draggedTemplate;
                    if (template) {
                        if (!template.html && template.loadContent) {
                            await template.loadContent();
                        }

                        templateId = template.id;
                        templateName = template.name;
                        htmlContent = template.html;
                    }
                } else if (dragData.data.blocks) {
                    // Old format with pre-extracted blocks
                    templateId = dragData.data.id || null;
                    templateName = dragData.data.name || null;
                    htmlContent = dragData.data.blocks
                        .map(block => block.data.content || '')
                        .join('\n');
                }
            }
        } catch (parseError) {
            // PRIORITY: Check global draggedTemplate first (for cross-iframe drags)
            if (window._alpineTemplates?.draggedTemplate) {
                const template = window._alpineTemplates.draggedTemplate;

                // Load template HTML if not already loaded
                if (!template.html && template.loadContent) {
                    await template.loadContent();
                }

                templateId = template.layoutId || template.id;
                templateName = template.name;
                htmlContent = template.html;
            }
            // Check if it's a simple drag type like 'Raw'
            else if (dragDataText === 'Raw' && window.templateDragData) {
                htmlContent = window.templateDragData.config.content;
                templateId = window.templateDragData.id || null;
                templateName = window.templateDragData.name || null;
            }
            // NEW: Handle simple template ID format (from form's drag handler)
            // Try custom template-id key first, then fall back to text/plain
            else if ((templateIdFromCustom || dragDataText) && window.alpineBlocksLayouts) {
                const lookupId = templateIdFromCustom || dragDataText;

                // Find template in global layouts array
                const template = resolveTemplate(lookupId, templateNameFromCustom);

                if (template) {
                    // Load template HTML if not already loaded
                    if (!template.html && template.loadContent) {
                        await template.loadContent();
                    }

                    templateId = template.layoutId || template.id;
                    templateName = template.name;
                    htmlContent = template.html;
                } else {
                    console.warn('⚠️  Template not found in alpineBlocksLayouts:', lookupId);
                }
            }
            // Fallback to raw HTML
            else {
                htmlContent = dragDataText;
            }
        }

        return { htmlContent, templateId, templateName };
    }

    /**
     * Clean HTML for save - remove indicators, convert empty zones to comments
     * @param {string} html - HTML content
     * @returns {string} Cleaned HTML
     */
    cleanupForSave(html) {
        if (!html || typeof html !== 'string') {
            return html;
        }

        // Create a temporary div to parse and manipulate the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Strip transient blocks-sidebar markers (tree ids + selection highlight) so
        // they never persist into saved content.
        tempDiv.querySelectorAll('[data-wps-tree-id]').forEach(el => el.removeAttribute('data-wps-tree-id'));
        tempDiv.querySelectorAll('[data-wps-highlight]').forEach(el => el.removeAttribute('data-wps-highlight'));

        // Strip the property schema from saved content — the settings panel resolves it
        // from the template registry by data-template-id (see TemplateSchemaResolver).
        tempDiv.querySelectorAll('script[data-template-properties]').forEach(el => el.remove());
        tempDiv.querySelectorAll('[data-template-properties]').forEach(el => el.removeAttribute('data-template-properties'));

        // Remove all drop indicators
        const dropIndicators = tempDiv.querySelectorAll('.richtext-drop-indicator');
        dropIndicators.forEach(indicator => indicator.remove());

        // Convert EMPTY nested drop zones back to <!-- drop --> comments
        // Drop zones with content (dropped templates) should be kept as-is
        const dropZones = tempDiv.querySelectorAll('.richtext-nested-dropzone');
        dropZones.forEach(zone => {
            // Check if drop zone is empty (only has the placeholder text)
            const textContent = zone.textContent.trim();
            const hasOnlyPlaceholder = textContent === 'Drop template here' || textContent === '';

            // Only convert to comment if empty
            if (hasOnlyPlaceholder) {
                const comment = document.createComment(' drop ');
                zone.parentNode.replaceChild(comment, zone);
            }
            // If it has content (a dropped template), leave it as-is with all its attributes
        });

        return tempDiv.innerHTML;
    }

    /**
     * Setup form cleanup handler
     * Ensures drop zones are cleaned up before form submission
     */
    setupFormCleanup() {
        const textarea = this.editor.getElement();
        if (!textarea) return;

        const form = textarea.closest('form');
        if (!form) return;

        const submitHandler = (e) => {
            // TinyMCE automatically syncs content via GetContent event
            // which already calls cleanupForSave()
            // This is just a backup to ensure cleanup happens
            const editorBody = this.editor.getBody();

            // Remove drop indicators from the editor itself
            const indicators = editorBody.querySelectorAll('.richtext-drop-indicator');
            indicators.forEach(ind => ind.remove());

            // Convert EMPTY drop zones back to comments in the live DOM
            const dropZones = editorBody.querySelectorAll('.richtext-nested-dropzone');
            dropZones.forEach(zone => {
                const textContent = zone.textContent.trim();
                const hasOnlyPlaceholder = textContent === 'Drop template here' || textContent === '';
                if (hasOnlyPlaceholder) {
                    const comment = document.createComment(' drop ');
                    zone.parentNode.replaceChild(comment, zone);
                }
            });

            // Force TinyMCE to save current content to textarea
            this.editor.save();
        };

        form.addEventListener('submit', submitHandler);

        // Store handler reference for cleanup
        if (!form._richTextSubmitHandlers) {
            form._richTextSubmitHandlers = [];
        }
        form._richTextSubmitHandlers.push(submitHandler);

        console.log('📝 Form cleanup handler attached');
    }

    /**
     * Cleanup plugin resources
     */
    destroy() {
        this.removeDropIndicator();

        // Remove injected styles from iframe
        const editorDoc = this.editor.getDoc();
        if (editorDoc) {
            const styleEl = editorDoc.getElementById(`dropzone-styles-${this.editor.id}`);
            if (styleEl) {
                styleEl.remove();
            }
        }

        console.log('🗑️ DropZonePlugin destroyed');
    }
}
